/**
 * Prices on the tip lists: "$ price" vs "odds %" display, and the combined odds
 * ("Odds NN%") of the tips shown.
 *
 * Where the prices come from:
 *  - Early Mail: Lexi's own odds (future-tips `impliedOdds`, copied into `currentPrice`).
 *    There is no market price 2 days out.
 *  - Race Day tips / Late Mail: the Betfair market price on each tip
 *    (`effectivePrice`, else `lastPriceTraded`, else `currentPrice`). These sets carry
 *    no Lexi odds.
 * "Odds %" is the same price shown as an implied chance (100 / price).
 */
import { isSameRunner, isScratchedInPodium } from "./tip-movement";

export const PRICE_DISPLAY = Object.freeze({
  PRICE: "price",
  PERCENT: "percent",
});
export const PRICE_DISPLAY_STORAGE_KEY = "tipsPriceDisplay";
export const PRICE_DISPLAY_EVENT = "tipsPriceDisplayChanged";

export const ODDS_TOTAL_HINT =
  "Sum of 1/price for the tips shown: the combined chance the prices give one of these tips. Not a prediction.";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "N/A") return null;
  const n = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

/** A price we can show: a positive number. */
export const toShownPrice = (value) => {
  const n = toNumber(value);
  return n !== null && n > 0 ? n : null;
};

/** A price that can count towards the combined odds: more than $1.00. */
export const toValidOddsPrice = (value) => {
  const n = toNumber(value);
  return n !== null && n > 1 ? n : null;
};

/**
 * The price a tip row shows: effectivePrice, else lastPriceTraded, else currentPrice
 * (Early Mail rows only have currentPrice = Lexi's odds).
 */
export const pickTipPrice = (tip) => {
  if (!tip || typeof tip !== "object") return null;
  return (
    toShownPrice(tip.effectivePrice) ??
    toShownPrice(tip.lastPriceTraded) ??
    toShownPrice(tip.currentPrice) ??
    null
  );
};

/** "$4.50" or "22.2%" (one decimal), "-" when there is no price. */
export const formatTipPrice = (price, mode = PRICE_DISPLAY.PRICE) => {
  const p = toShownPrice(price);
  if (p === null) return "-";
  if (mode === PRICE_DISPLAY.PERCENT) return `${(100 / p).toFixed(1)}%`;
  return `$${p.toFixed(2)}`;
};

/**
 * Combined odds of the tips shown: sum of 1/price as a whole percent.
 *
 * @param {Array<{ price: any, locked?: boolean, scratched?: boolean }>} items - in list order
 * @param {Object} [options]
 * @param {number} [options.shownCount] - only the first N items count (2 or 4 per Tip Display Mode)
 * @returns {{ percent: number, count: number } | null} null when no tip has a valid price
 */
export const getOddsTotal = (items, { shownCount } = {}) => {
  const list = Array.isArray(items) ? items : [];
  const limit =
    Number.isFinite(shownCount) && shownCount > 0 ? shownCount : list.length;
  let sum = 0;
  let count = 0;
  list.slice(0, limit).forEach((item) => {
    if (!item || item.locked || item.scratched) return;
    const price = toValidOddsPrice(item.price);
    if (price === null) return;
    sum += 1 / price;
    count += 1;
  });
  if (count === 0) return null;
  return { percent: Math.round(sum * 100), count };
};

/** "Odds 89%" (owner's wording). */
export const formatOddsTotal = (total) =>
  total ? `Odds ${total.percent}%` : "";

const idOf = (value) => {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s || null;
};

/**
 * Was this runner scratched, per the race results? Uses the scratchings feed
 * (Betfair selectionIds) and the archived podium arrays. Early Mail tips have no
 * selectionId, so it is borrowed from the same runner in the race's other tips.
 */
export const isRunnerScratched = (runner, raceResults, otherTips = []) => {
  if (!runner || !raceResults) return false;
  if (isScratchedInPodium(runner, raceResults)) return true;

  const scratched = new Set(
    (Array.isArray(raceResults.betfairResults?.scratchings)
      ? raceResults.betfairResults.scratchings
      : []
    )
      .map((s) => (s && typeof s === "object" ? idOf(s.selectionId) : idOf(s)))
      .filter(Boolean),
  );
  if (scratched.size === 0) return false;

  const ownId = idOf(runner.selectionId);
  if (ownId) return scratched.has(ownId);

  const pool = [
    ...(Array.isArray(otherTips) ? otherTips : []),
    ...(raceResults.predictionData?.firstFourData || []),
    ...(raceResults.earlyPredictionData?.firstFourData || []),
  ];
  const twin = pool.find(
    (tip) =>
      tip &&
      typeof tip === "object" &&
      idOf(tip.selectionId) &&
      isSameRunner(runner, tip),
  );
  return twin ? scratched.has(idOf(twin.selectionId)) : false;
};
